/**
 * The per-target "Full report" affordance: request one, watch it, download it.
 *
 * Four states with no ambiguity between them, because each means something
 * different to the person clicking:
 *
 *   nothing yet   — "Generate report", which opens the confirmation
 *   queued        — the row exists but the runtime has not been reached
 *   running       — the reporter is investigating; this is minutes, not seconds
 *   sent | failed — terminal. `sent` offers the download, `failed` shows why
 *
 * And `sent` is two states, not one: `downloadable` is a conjunction the API
 * computes, because a report can be `sent` with the stored copy already expired out
 * of the bucket by the 90-day lifecycle rule. When it has, the PDF attached to the
 * e-mail is still the document — which is the whole message that state has to
 * carry, so it is visible text and not a hover title.
 *
 * ── `created: false` is a success ──
 *
 * A second request for a target whose report is already `queued` or `running`
 * returns the existing row with `created: false`. That is the API declining to
 * start a second multi-dollar job, not an error, and the copy has to say so
 * *before* the click as well as after: the confirmation states it, and the
 * in-flight state says outright that asking again gets this report rather than
 * another one. The receipt afterwards is the API's own sentence, rendered
 * verbatim by the shell.
 *
 * Rendered inside a row's expanded detail, in both tables — not as a column. A
 * report is an action costing several dollars and fifteen minutes, and a column of
 * them invites a mis-click while scanning a ranking; behind the chevron, the
 * person has already asked to look at this one target.
 *
 * It works on a **board** row too, which needs one thing the ranking does not: a
 * report is scoped to `(run_id, target_id)` and a board entry is a consolidation
 * across runs with neither. So the caller passes the run whose row is being
 * assessed — the newest observation — and `runLabel` names it in the
 * confirmation, since "a report on ACCase" and "a report on what run X concluded
 * about ACCase" are different documents.
 *
 * The confirmation is not a formality: this mails a document carrying
 * confidential internal protein ids and spends several dollars of Sol doing it.
 * The recipient shown is the signed-in account, and the copy says the server
 * takes the address from the token — because the address in the modal is not what
 * the server sends to, it is only what the browser believes it should be.
 *
 * ── Motion ──
 *
 * The only spinner here is on a button whose own request is in flight, which is a
 * transient the reader caused. A report that is `queued` or `running` for fifteen
 * minutes is a *state*, so it is a badge and a sentence rather than something
 * spinning in the corner of the page for a quarter of an hour.
 */
import { useState } from 'react'
import { Download, FileText, Loader2, MailCheck } from 'lucide-react'
import { ConfirmDialog } from './ConfirmDialog'
import { Badge } from './ui/Badge'
import { Button } from './ui/Button'
import { FailedState } from './ui/States'
import { formatTimestamp } from '../lib/utils'
import { REPORT_IN_FLIGHT, type ReportRow } from '../api/types'

interface Props {
  /** `leaderboard.targets.id` — one run's row, not a board identity. */
  targetId: number
  protein: string
  accession: string | null
  /**
   * The run to assess, when it is not the one selected on screen. Passed straight
   * to `onRequest`; omit it on the per-run table, where the hook already knows.
   */
  runId?: string
  /** Shown in the confirmation when the run is not the one on screen. */
  runLabel?: string
  /** The newest report for this target, or undefined if none was ever asked for. */
  report: ReportRow | undefined
  /** The MSAL account's address, for the confirmation. Empty when auth is off. */
  accountEmail: string
  requesting: boolean
  downloading: boolean
  /** Why this target's last request or download failed. */
  error: string | null
  onRequest: (targetId: number, runId?: string) => void
  onDownload: (report: ReportRow) => void
}

export function ReportButton({
  targetId,
  protein,
  accession,
  runId,
  runLabel,
  report,
  accountEmail,
  requesting,
  downloading,
  error,
  onRequest,
  onDownload,
}: Props) {
  const [confirming, setConfirming] = useState(false)

  // The same list the polling hook reads, so "in flight" cannot mean two things.
  const inFlight = report !== undefined && REPORT_IN_FLIGHT.includes(report.status)

  return (
    <div className="flex flex-col items-start gap-2">
      {inFlight ? (
        <InFlight report={report} />
      ) : report?.status === 'sent' ? (
        <Sent
          report={report}
          downloading={downloading}
          requesting={requesting}
          onDownload={onDownload}
          onRequestAgain={() => setConfirming(true)}
        />
      ) : (
        <div className="flex flex-col items-start gap-1.5">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConfirming(true)}
            disabled={requesting}
            title="Commission a fresh investigation of this target and have it emailed to you as a PDF."
          >
            {requesting ? (
              <Loader2 aria-hidden="true" className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <FileText aria-hidden="true" className="h-3.5 w-3.5" />
            )}
            Generate report
          </Button>
          {/* `failed` is terminal and it is the reason a reader is looking here, so
              the stored reason is on the page rather than behind a hover. It can be
              a whole exception message; it wraps rather than being clipped, because
              the tail of a stack trace is often the only part that says anything. */}
          {report?.status === 'failed' && (
            <div className="flex flex-col items-start gap-1">
              <Badge variant="red">Last attempt failed</Badge>
              <p className="max-w-md break-words text-2xs leading-relaxed text-slate-400">
                {report.error ?? 'No reason was recorded.'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Verbatim, with `role="alert"`: this is the API's own `detail` — a 403
          explaining that reports need authentication, a 400 that the address is
          outside the allowed domains. Both are answers, not stack traces. */}
      {error && <FailedState message={error} className="max-w-md" />}

      {confirming && (
        <ConfirmDialog
          title={`Generate a full report on ${protein}?`}
          confirmLabel="Generate and email"
          busy={requesting}
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            onRequest(targetId, runId)
            setConfirming(false)
          }}
        >
          <p>
            A dedicated reporting agent will re-investigate{' '}
            <span className="font-medium text-slate-100">{protein}</span>
            {accession && (
              <>
                {' '}
                (<span className="font-mono text-xs">{accession}</span>)
              </>
            )}{' '}
            from the knowledge graph, the literature and the structure databases —
            it does not summarise the stored rationale, it checks it. Anything it
            cannot verify is reported as unverifiable rather than dropped.
          </p>
          {/* Only when the report is for a run other than the selected one, i.e.
              from the board. A report assesses ONE run's hypothesis — its
              rationale, its review verdicts, its Elo history — so which run is
              part of what is being asked for, not a technical detail. */}
          {runLabel && (
            <p>
              It assesses what run{' '}
              <span className="font-mono text-xs text-slate-300">{runLabel}</span>{' '}
              concluded — the most recent run that ranked this target. Earlier runs’
              observations of it are listed above and are not what this report is
              about.
            </p>
          )}
          <p>
            The finished assessment is emailed as a PDF to{' '}
            <span className="font-medium text-slate-100">
              {accountEmail || 'your signed-in address'}
            </span>
            , and stays downloadable from this table for 90 days. The address comes
            from your sign-in token, not from this page — if that is not where you
            want it, sign in as the right account.
          </p>
          {/* The `created: false` path, stated before the click. Someone else may
              have asked for this report a minute ago and the page not know it yet,
              so this is reachable from a button that looks idle. */}
          <p>
            If a report on this target is already queued or being written, you get
            that one — the same document, one bill. A second job is never started.
          </p>
          <p className="text-xs text-slate-400">
            Takes roughly 5–15 minutes and costs a few dollars of model time. The
            document contains unpublished internal target assessments: do not
            forward it outside Corteva.
          </p>
        </ConfirmDialog>
      )}
    </div>
  )
}

/**
 * `queued` or `running`.
 *
 * No button, because there is nothing to click: cancelling is not offered — the
 * work is already running in another container and a half-written assessment is
 * not a useful thing to keep — and requesting again would return this same row.
 * That last part is the sentence, not a tooltip: without it, a reader who wants
 * their report sooner has no way to know that clicking again would achieve
 * nothing, and the affordance is missing rather than explained.
 */
function InFlight({ report }: { report: ReportRow }) {
  const queued = report.status === 'queued'
  return (
    <div className="flex flex-col items-start gap-1">
      <Badge variant="blue">{queued ? 'Queued' : 'Being written'}</Badge>
      <p className="max-w-md text-2xs leading-relaxed text-slate-400">
        {queued
          ? 'Recorded. Waiting for the agent runtime to pick it up.'
          : `Investigating since ${formatTimestamp(report.updated_at)}. Usually 5–15 minutes; the e-mail arrives when it is done.`}{' '}
        Asking again gets this report, not a second one.
      </p>
    </div>
  )
}

/**
 * `sent`, in its two forms.
 *
 * `downloadable` and not `status === 'sent'` decides whether the download is
 * offered — the API computes the conjunction, and an expired document behind an
 * enabled button is a 404 the reader cannot explain. What the expired form has to
 * say is that nothing was lost: the PDF attached to the e-mail is the same
 * document, and it is the only copy now.
 */
function Sent({
  report,
  downloading,
  requesting,
  onDownload,
  onRequestAgain,
}: {
  report: ReportRow
  downloading: boolean
  requesting: boolean
  onDownload: (report: ReportRow) => void
  onRequestAgain: () => void
}) {
  return (
    <div className="flex flex-col items-start gap-1.5">
      {report.downloadable ? (
        <>
          <Button
            size="sm"
            onClick={() => onDownload(report)}
            disabled={downloading}
            title={`Emailed to ${report.recipient}. Download the same document.`}
          >
            {downloading ? (
              <Loader2 aria-hidden="true" className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download aria-hidden="true" className="h-3.5 w-3.5" />
            )}
            Download report
          </Button>
          <p className="max-w-md text-2xs leading-relaxed text-slate-400">
            E-mailed to{' '}
            <span className="font-mono">{report.recipient}</span> on{' '}
            {formatTimestamp(report.sent_at)}.
          </p>
        </>
      ) : (
        <>
          <Badge variant="default">
            <MailCheck aria-hidden="true" className="h-3 w-3" />
            Sent, stored copy expired
          </Badge>
          <p className="max-w-md text-2xs leading-relaxed text-slate-400">
            E-mailed to <span className="font-mono">{report.recipient}</span> on{' '}
            {formatTimestamp(report.sent_at)}. Reports are kept here for 90 days
            and this one has aged out — the PDF attached to that e-mail is the same
            document. Generate a fresh one if you need it back in this table.
          </p>
        </>
      )}
      {/* A second report for the same target is allowed — asking again after a
          rubric change is legitimate — so the request affordance stays. */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onRequestAgain}
        disabled={requesting}
      >
        {requesting && (
          <Loader2 aria-hidden="true" className="h-3.5 w-3.5 animate-spin" />
        )}
        Generate a new one
      </Button>
    </div>
  )
}
